import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, CircularProgress, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField } from '@mui/material';
import administradorDbService from '../../services/administradorDbService';
import { MigrationLog, MigrationModalProps } from '../../types';

const MigrationModal: React.FC<MigrationModalProps> = ({ open, onClose, banco }) => {
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [migrating, setMigrating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [logsPerPage] = useState<number>(5);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);
  const [newLogIndex, setNewLogIndex] = useState<number | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      if (!banco) return;

      try {
        setLoading(true);
        const response = await administradorDbService.getMigrationLogs(banco);
        setLogs(response);
        setLoading(false);
      } catch (error) {
        setError('Erro ao carregar logs de migração.');
        setLoading(false);
      }
    };

    if (open && banco) {
      fetchLogs();
    }
  }, [open, banco]);

  const handleRunMigration = async () => {
    if (!banco) return;

    if (!password) {
      setPasswordError(true);
      return;
    }

    try {
      setMigrating(true);
      const response = await administradorDbService.runMigrations({ banco, senha: password });

      const newLog = response.logs[0];
      setLogs([newLog, ...logs]);
      setNewLogIndex(0); 

      setMigrating(false);
      setError(null);
      setPassword('');
      setPasswordError(false); 
      setShowPasswordField(false);
    } catch (error) {
      setError('Erro ao executar migração.');
      setMigrating(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setLogs([]);
    setLoading(false);
    setPassword('');
    setPasswordError(false);
    setShowPasswordField(false);
    setNewLogIndex(null);
    setPage(1);
    onClose();
  };
  

  const indexOfLastLog = page * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = logs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(logs.length / logsPerPage);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Migrações Realizadas</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Banco</TableCell>
                    <TableCell>Comando</TableCell>
                    <TableCell>Saída</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentLogs.map((log, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index === newLogIndex ? 'lightgreen' : 'transparent', 
                      }}
                    >
                      <TableCell>{log.banco}</TableCell>
                      <TableCell>{log.comando}</TableCell>
                      <TableCell>{log.saida}</TableCell>
                      <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Paginação */}
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
              <Button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                variant="outlined"
                sx={{ marginRight: 1 }}
              >
                Anterior
              </Button>
              <Button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                variant="outlined"
              >
                Próximo
              </Button>
            </Box>
          </>
        )}

        {migrating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error" sx={{ marginTop: 2 }}>{error}</Typography>}

        {/* Exibe o campo de senha apenas quando o usuário clica em "Nova Migração" */}
        {showPasswordField && (
          <Box sx={{ marginTop: 2 }}>
            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              helperText={passwordError ? "A senha é obrigatória" : ""}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
        <Button
          onClick={() => setShowPasswordField(true)} 
          color="secondary"
          variant="contained"
          disabled={migrating}
        >
          {migrating ? 'Executando...' : 'Nova Migração'}
        </Button>
        {showPasswordField && (
          <Button
            onClick={handleRunMigration}
            color="secondary"
            variant="contained"
            disabled={migrating}
          >
            {migrating ? 'Executando...' : 'Realizar Migração'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MigrationModal;
